import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';

const TRANSITION = 'cubic-bezier(0.4, 0, 0.2, 1)';
const REVEAL_MS = 500;

/**
 * LazyBackgroundImage — background image with preloading and smooth reveal
 *
 * FLOW:
 * 1. If critical or in view → load placeholder (if any) with high priority, then main image (low priority when critical for LCP).
 * 2. Background layer shows: placeholder (when ready) OR blurred main (blur-up; on by default) OR solid color. Never blurs content.
 * 3. When main image loads → show it in a fresh <img> (so GIFs start at frame 0) with fade-in. If `placeholderImage` is set, that layer stays visible under the fading-in main image until the fade completes (no white flash).
 * 4. When `src` changes on the same instance (e.g. client navigation), the new image crossfades over the previous frame instead of flashing.
 *
 * LAYERS (bottom to top): BackgroundLayer → MainImageLayer → [OverlayLayer if overlay prop] → children (content)
 * Overlay is inside the same container as the image, so dimensions always match.
 */

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  border-radius: ${({ $rounded }) => ($rounded ? '24px' : '0')};
  overflow: hidden;
`;

const ImageLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

const mainFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const MainImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  pointer-events: none;
  z-index: 0;
  animation: ${mainFadeIn} ${({ $fadeSec = 0.5 }) => $fadeSec}s ease-out forwards;
`;

/** Sharp layer: no entrance animation (used after first paint and as crossfade base). */
const MainImgStack = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  pointer-events: none;
  z-index: 0;
`;

/** Fades in over MainImgStack when src changes so swaps do not flash white. */
const MainImgCrossfade = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  pointer-events: none;
  z-index: 0.25;
  opacity: ${({ $opacity }) => $opacity};
  transition: opacity ${({ $durationMs }) => $durationMs}ms ${TRANSITION};
`;

const OverlayLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0.5;
  pointer-events: none;
`;

const LazyBackgroundImage = ({
  src,
  children,
  borderRadius,
  placeholderColor = '#f5f5f5',
  placeholderImage = null,
  backgroundSize = 'cover',
  backgroundPosition = 'center',
  backgroundRepeat = 'no-repeat',
  critical = false,
  transitionDuration: transitionDurationProp,
  useBlurPlaceholder: useBlurPlaceholderProp,
  rounded = true,
  overlay,
  onLoad,
  onError,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  role = 'img',
  className = '',
  style = {},
  ...props
}) => {
  const transitionDuration =
    transitionDurationProp ?? (critical ? 0.6 : 0.45);
  const useBlurPlaceholder = useBlurPlaceholderProp ?? true;
  const [inView, setInView] = useState(critical);
  const [placeholderReady, setPlaceholderReady] = useState(!placeholderImage);
  /** First sharp image is shown with MainImg + keyframe fade-in; later swaps use stack + crossfade. */
  const [stackSrc, setStackSrc] = useState(null);
  const [coverSrc, setCoverSrc] = useState(null);
  const [coverOpacity, setCoverOpacity] = useState(0);
  /** After first src→src transition we keep the base layer on MainImgStack (no keyframe re-run). */
  const [usedCrossfade, setUsedCrossfade] = useState(false);
  const stackSrcRef = useRef(null);
  const loadGenerationRef = useRef(0);
  const fadeCommitRef = useRef(null);
  const ref = useRef(null);

  const mainLoaded = Boolean(stackSrc);
  const crossfadeMs = Math.round(Math.max(transitionDuration, 0.35) * 1000);
  /** Keep placeholder (or blur) visible under MainImg until first-paint fade completes — avoids white flash. */
  const [placeholderUnderMainDone, setPlaceholderUnderMainDone] = useState(() => !placeholderImage);

  useEffect(() => {
    setPlaceholderUnderMainDone(!placeholderImage);
  }, [src, placeholderImage]);

  // 1) When to load: critical → immediately; else when in viewport (IntersectionObserver)
  useEffect(() => {
    if (!src) return;
    if (critical) {
      setInView(true);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin: '400px', threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [src, critical]);

  // 2) Load placeholder (high priority when critical)
  useEffect(() => {
    if (!placeholderImage) return;
    const img = new Image();
    if (critical && img.fetchPriority !== undefined) img.fetchPriority = 'high';
    img.onload = () => setPlaceholderReady(true);
    img.onerror = () => setPlaceholderReady(true);
    img.src = placeholderImage;
  }, [placeholderImage, critical]);

  // 3) Load main image; first paint uses fade-in, subsequent src changes crossfade over previous frame
  useEffect(() => {
    if (!inView || !src) return;
    const generation = ++loadGenerationRef.current;
    let cancelled = false;

    const img = new Image();
    if (critical && img.fetchPriority !== undefined) img.fetchPriority = 'low';

    const finish = async () => {
      if (cancelled || generation !== loadGenerationRef.current) return;
      try {
        if (img.decode) await img.decode();
      } catch (_) {
        /* decode optional */
      }
      if (cancelled || generation !== loadGenerationRef.current) return;

      const resolved = img.src || src;
      const prev = stackSrcRef.current;

      if (!prev) {
        stackSrcRef.current = resolved;
        setStackSrc(resolved);
        onLoad?.();
        return;
      }

      if (prev === resolved) return;

      setUsedCrossfade(true);
      fadeCommitRef.current = resolved;
      setCoverSrc(resolved);
      setCoverOpacity(0);
      requestAnimationFrame(() => {
        if (cancelled || generation !== loadGenerationRef.current) return;
        setCoverOpacity(1);
      });
    };

    img.onload = () => {
      finish();
    };
    img.onerror = () => {
      if (cancelled || generation !== loadGenerationRef.current) return;
      const resolved = src;
      const prev = stackSrcRef.current;
      if (!prev) {
        stackSrcRef.current = resolved;
        setStackSrc(resolved);
        onError?.();
        return;
      }
      if (prev === resolved) return;
      setUsedCrossfade(true);
      fadeCommitRef.current = resolved;
      setCoverSrc(resolved);
      setCoverOpacity(0);
      requestAnimationFrame(() => {
        if (cancelled || generation !== loadGenerationRef.current) return;
        setCoverOpacity(1);
      });
    };
    img.src = src;

    return () => {
      cancelled = true;
      fadeCommitRef.current = null;
      setCoverSrc(null);
      setCoverOpacity(0);
    };
  }, [inView, src, critical, onLoad, onError]);

  useEffect(() => {
    if (!mainLoaded || !placeholderImage || !placeholderReady) return undefined;
    const ms = Math.round(transitionDuration * 1000) + 80;
    const t = window.setTimeout(() => setPlaceholderUnderMainDone(true), ms);
    return () => window.clearTimeout(t);
  }, [mainLoaded, placeholderImage, placeholderReady, transitionDuration]);

  const handleCrossfadeEnd = useCallback((e) => {
    if (e.propertyName !== 'opacity') return;
    if (coverOpacity !== 1) return;
    const pending = fadeCommitRef.current;
    if (!pending || pending !== coverSrc) return;
    fadeCommitRef.current = null;
    stackSrcRef.current = pending;
    setStackSrc(pending);
    setCoverSrc(null);
    setCoverOpacity(0);
  }, [coverOpacity, coverSrc]);

  // What the background layer shows (never the sharp main — that’s MainImg)
  const backgroundImage =
    mainLoaded && placeholderUnderMainDone
      ? 'none'
      : placeholderImage && placeholderReady
        ? `url("${placeholderImage}")`
        : useBlurPlaceholder && src && !mainLoaded
          ? `url("${src}")`
          : 'none';

  const backgroundColor =
    !mainLoaded && !placeholderImage ? placeholderColor : 'transparent';

  // Blur only the background layer, only when showing main URL before it’s loaded (blur-up)
  const blurBackground =
    useBlurPlaceholder &&
    src &&
    !mainLoaded &&
    !(placeholderImage && placeholderReady);

  // Reveal background when we have something to show (placeholder or blur-up)
  const showBackground =
    (placeholderImage && placeholderReady && (!mainLoaded || !placeholderUnderMainDone)) ||
    (useBlurPlaceholder && src && !mainLoaded);
  const opacity = showBackground ? 1 : 0;

  const transitionMs = transitionDuration * 1000;

  const contentChild =
    React.isValidElement(children) ?
      React.cloneElement(children, {
        style: {
          ...children.props.style,
          position: 'relative',
          zIndex: 1,
          backgroundImage: 'none',
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      })
    : children;

  return (
    <Wrapper
      ref={ref}
      $rounded={rounded}
      className={className}
      style={style}
      role={ariaLabel || ariaLabelledBy ? role : undefined}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      {...props}
    >
      <ImageLayer
        style={{
          backgroundImage,
          backgroundColor,
          backgroundSize,
          backgroundPosition,
          backgroundRepeat,
          opacity,
          filter: blurBackground ? 'blur(20px)' : 'none',
          transform: mainLoaded ? 'scale(1)' : 'scale(1.02)',
          transition: [
            `background-image ${transitionMs}ms ${TRANSITION}`,
            `opacity ${REVEAL_MS}ms ${TRANSITION}`,
            `filter ${transitionMs}ms ease-out`,
            `transform ${transitionMs}ms ${TRANSITION}`,
          ].join(', '),
        }}
      />
      {stackSrc && !coverSrc && !usedCrossfade && (
        <MainImg
          src={stackSrc}
          alt=""
          aria-hidden
          decoding="async"
          style={{ objectPosition: backgroundPosition }}
          $fadeSec={transitionDuration}
        />
      )}
      {stackSrc && !coverSrc && usedCrossfade && (
        <MainImgStack
          src={stackSrc}
          alt=""
          aria-hidden
          decoding="async"
          style={{ objectPosition: backgroundPosition }}
        />
      )}
      {stackSrc && coverSrc && (
        <>
          <MainImgStack
            src={stackSrc}
            alt=""
            aria-hidden
            decoding="async"
            style={{ objectPosition: backgroundPosition }}
          />
          <MainImgCrossfade
            src={coverSrc}
            alt=""
            aria-hidden
            decoding="async"
            style={{ objectPosition: backgroundPosition }}
            $opacity={coverOpacity}
            $durationMs={crossfadeMs}
            onTransitionEnd={handleCrossfadeEnd}
          />
        </>
      )}
      {overlay != null && (
        <OverlayLayer
          style={{
            background: typeof overlay === 'number'
              ? `rgba(0, 0, 0, ${overlay})`
              : overlay,
          }}
        />
      )}
      {contentChild}
    </Wrapper>
  );
};

export default LazyBackgroundImage;
